// ============================================================================
// psyWorkspace.ts — ДАННЫЕ рабочего места психолога и КАБИНЕТА ПАЦИЕНТА
// (модуль hydra.medic.psy).
//
// SSoT — сервер: пациенты/приёмы ядра «ГидраМедик», заметки/заключения/
// назначения модуля, методики — движок опросов ядра. Здесь только чтение/
// запись существующими RPC (новых серверных механизмов нет) и маппинг строк
// ListTableData в типизированные модели. Чтение — через durable-кэш (офлайн-
// фёрст), запись — dataService (онлайн → сервер, офлайн → очередь).
//
// КАБИНЕТ ПАЦИЕНТА: назначения читаются из psy_assignments (SSoT), протоколы —
// из физических таблиц типов опросов по subject_id пациента. Прохождение — НЕ
// здесь: пациент открывает существующую публичную страницу /poll/... (ссылка
// назначения или короткий код), поэтому второго раннера/второго протокола нет.
// ============================================================================

import type { Poll, PollTypeInfo, TableDataRow } from '@zdc/sdk';
import { getSDK } from '@/store/sdkStore';
import { useCenterStore } from '@/store/centerStore';
import { dataService } from '@/lib/dataService';
import { readThrough } from '@/lib/offlineCache';
import { isNotFoundError, isPermissionDeniedError, logSwallowed } from '@/lib/errors';

export { PSY_ROLE, PSY_INTERN_ROLE } from '@/lib/securityRoles';

export const PATIENTS = 'medic_patients';
export const ENCOUNTERS = 'medic_encounters';
export const NOTES = 'psy_notes';
export const CONCLUSIONS = 'psy_conclusions';
export const ASSIGNMENTS = 'psy_assignments';

/** Доступная методика (шаблон + тип опроса = физтаблица протоколов). */
export interface PsyMethod {
  code: string;
  name: string;
  synonym: string;
  description: string;
  scoring: string;
  status: string;
  templateId: string;
}

/** Строка протокола (результат) пациента по конкретной методике. */
export interface PatientResult {
  id: string;
  methodCode: string;
  methodName: string;
  createdAt: string;
  score: string;
  passed: boolean;
  conclusion: string;
}

/** Назначение методики пациенту. */
export interface PsyAssignment {
  id: string;
  patientId: string;
  methodCode: string;
  status: string;
  assignedDate: string;
  linkToken: string;
  linkExpiresAt: string;
}

/** Активная ссылка прохождения (для повторного показа без создания новой). */
export interface ActiveLink {
  token: string;
  expiresAt: string;
}

function slug(): string {
  return useCenterStore.getState().baseSlug || 'local';
}

function cellsOf(row: TableDataRow): Record<string, string> {
  const out: Record<string, string> = {};
  for (const v of row.values) out[String(v.column)] = String(v.value ?? '');
  return out;
}

function rowsOf(rows: TableDataRow[]): Array<Record<string, string> & { id: string }> {
  return rows.map((row) => ({ id: String(row.id || ''), ...cellsOf(row) }));
}

function isTrue(value: string): boolean {
  const s = String(value || '').trim().toLowerCase();
  return s === 'true' || s === '1' || s === 'да';
}

/** Методики: типы опросов (коды протоколов) + шаблоны (описание/движок). */
export async function loadMethods(): Promise<PsyMethod[]> {
  const res = await readThrough(`psy:methods:${slug()}`, async () => {
    const sdk = getSDK();
    const [types, polls] = await Promise.all([sdk.poll.listPollTypes({}), sdk.poll.listPolls({})]);
    const pollById = new Map<string, Poll>();
    for (const p of polls.items) pollById.set(String(p.id), p);
    return types.items
      .map((t: PollTypeInfo) => {
        const poll = pollById.get(String(t.templateId));
        return {
          code: String(t.code || ''),
          name: String(t.name || ''),
          synonym: String(t.synonym || t.name || t.code || ''),
          description: String(poll?.description || ''),
          scoring: String(t.scoring || poll?.scoring || ''),
          status: String(t.status || poll?.status || ''),
          templateId: String(t.templateId || ''),
        };
      })
      .filter((m) => m.code && m.templateId)
      .sort((a, b) => a.synonym.localeCompare(b.synonym, 'ru'));
  });
  return res.data;
}

/** Назначения пациента (только его строки — отбор на сервере). */
export async function loadAssignments(patientId: string): Promise<PsyAssignment[]> {
  if (!patientId) return [];
  const res = await readThrough(`psy:assignments:${slug()}:${patientId}`, async () => {
    const r = await getSDK().metadata.listTableData({
      schema: 'public', table: ASSIGNMENTS, pageSize: 200, page: 0,
      filters: [{ column: 'patient_id', value: patientId }],
    });
    return rowsOf(r.rows).map((row) => ({
      id: row.id,
      patientId: row.patient_id || patientId,
      methodCode: row.method_code || '',
      status: row.status || 'assigned',
      assignedDate: row.assigned_date || '',
      linkToken: row.link_token || '',
      linkExpiresAt: row.link_expires_at || '',
    }));
  });
  return res.data;
}

/** Загрузка протоколов: список + признак «права чтения не выданы». */
export interface ResultsLoad {
  items: PatientResult[];
  /** Хотя бы один тип опроса закрыт правами роли — кабинет сообщает это явно. */
  denied: boolean;
}

/**
 * Результаты пациента по ВСЕМ методикам (назначенным и нет). Отказ в праве
 * чтения — НЕ пустая таблица: возвращается флаг denied (кабинет показывает
 * честное ограничение доступа). Тип без физической таблицы — журнал, не ошибка.
 */
async function loadResultsForMethods(methods: PsyMethod[], patientId: string): Promise<ResultsLoad> {
  const sdk = getSDK();
  const out: PatientResult[] = [];
  for (const m of methods) {
    try {
      const r = await sdk.metadata.listTableData({
        schema: 'public', table: m.code, pageSize: 100, page: 0,
        filters: [{ column: 'subject_id', value: patientId }],
      });
      for (const row of rowsOf(r.rows)) {
        out.push({
          id: row.id,
          methodCode: m.code,
          methodName: m.synonym,
          createdAt: row.created_at || '',
          score: row.score || '',
          passed: isTrue(row.passed || ''),
          conclusion: row.conclusion || '',
        });
      }
    } catch (e) {
      if (isPermissionDeniedError(e)) return { items: out, denied: true };
      if (!isNotFoundError(e)) throw e;
      logSwallowed(e, `psyWorkspace: результаты ${m.code}`);
    }
  }
  out.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  return { items: out, denied: false };
}

/**
 * loadPatientResultsForMethods — протоколы пациента по УЖЕ загруженному банку
 * методик (кадр кабинета: методы читаются отдельно, чтобы отказ в доступе к
 * назначениям не скрывал результаты).
 */
export async function loadPatientResultsForMethods(
  methods: PsyMethod[],
  patientId: string,
): Promise<ResultsLoad> {
  if (!patientId || methods.length === 0) return { items: [], denied: false };
  return loadResultsForMethods(methods, patientId);
}

/** Результаты пациента (+ признак отказа в правах) — единая точка чтения. */
export async function loadPatientResultsDetailed(patientId: string): Promise<ResultsLoad> {
  if (!patientId) return { items: [], denied: false };
  const methods = await loadMethods();
  const res = await readThrough(
    `psy:results2:${slug()}:${patientId}`,
    () => loadResultsForMethods(methods, patientId),
  );
  return res.data;
}

/** Результаты пациента (для рабочего места психолога — только список). */
export async function loadPatientResults(patientId: string): Promise<PatientResult[]> {
  return (await loadPatientResultsDetailed(patientId)).items;
}

/** Назначение по коду методики: активное (assigned) приоритетнее истории. */
export function assignmentFor(assignments: PsyAssignment[], methodCode: string): PsyAssignment | null {
  const list = assignments.filter((a) => a.methodCode === methodCode);
  if (list.length === 0) return null;
  const active = [...list].reverse().find((a) => a.status === 'assigned');
  return active || list[list.length - 1];
}

/** Активная ссылка назначения (не истекла) — без обращения к серверу. */
export function activeLink(a: PsyAssignment | null): ActiveLink | null {
  if (!a || !a.linkToken) return null;
  if (a.linkExpiresAt) {
    const t = Date.parse(a.linkExpiresAt);
    if (!Number.isNaN(t) && t <= Date.now()) return null;
  }
  return { token: a.linkToken, expiresAt: a.linkExpiresAt };
}

/** Код для ручного ввода (первые 8 символов токена) и полный URL. */
export function shortCode(token: string): string {
  return token.slice(0, 8).toLowerCase();
}

export function pollLink(origin: string, token: string): string {
  const base = String(origin || '').replace(/\/+$/, '');
  return `${base}/poll/${slug()}/${token}`;
}

/** Страница ввода короткого кода (тот же PollGuestPage, без токена). */
export function pollCodeEntry(origin: string): string {
  const base = String(origin || '').replace(/\/+$/, '');
  return `${base}/poll/${slug()}`;
}

/** Подпись статуса назначения (коды перечисления PsyAssignmentStatus). */
export function assignmentStatusLabel(status: string): string {
  const code = String(status || '').trim().toLowerCase();
  if (code === 'assigned') return 'Назначена';
  if (code === 'done') return 'Выполнена';
  if (code === 'cancelled') return 'Отменена';
  return status || 'Назначена';
}

/** Действующее назначение (ещё не выполнено и не отменено). */
export function isAssignmentActive(a: PsyAssignment): boolean {
  return String(a.status || '').trim().toLowerCase() === 'assigned';
}

/** Тексты заключений из ответа движка (conclusions_json) — единый разбор. */
export function parseConclusions(json: string): string[] {
  try {
    const parsed: unknown = JSON.parse(json || '{}');
    if (!parsed || typeof parsed !== 'object') return [];
    return Object.values(parsed as Record<string, unknown>)
      .map((v) => String(v ?? '').trim())
      .filter((s) => s !== '');
  } catch (e) {
    logSwallowed(e, 'psyWorkspace: разбор заключения');
    return [];
  }
}

/** Строка кабинета: назначение + методика банка + активная ссылка + результат. */
export interface CabinetRow {
  assignment: PsyAssignment;
  method: PsyMethod | null;
  link: ActiveLink | null;
  lastResult: PatientResult | null;
}

/** Данные кабинета: банк методик, назначения, протоколы (+ отказ в правах). */
export interface PsyCabinetData {
  methods: PsyMethod[];
  assignments: PsyAssignment[];
  results: PatientResult[];
  resultsDenied: boolean;
}

/**
 * Единая загрузка кабинета пациента (SRP: компонент только рендерит).
 * Назначения/протоколы читаются существующими RPC; права — SSoT сервера.
 */
export async function loadPsyCabinet(patientId: string): Promise<PsyCabinetData> {
  const [methods, assignments, results] = await Promise.all([
    loadMethods(),
    loadAssignments(patientId),
    loadPatientResultsDetailed(patientId),
  ]);
  return { methods, assignments, results: results.items, resultsDenied: results.denied };
}

/**
 * Разбор назначений на «действующие» и «историю» (свежие сверху). Для каждого
 * назначения подставляется методика банка, активная ссылка и последний результат
 * по этой методике — без повторной загрузки данных.
 */
export function buildCabinetRows(data: PsyCabinetData): { active: CabinetRow[]; history: CabinetRow[] } {
  const methodByCode = new Map(data.methods.map((m) => [m.code, m]));
  const rows: CabinetRow[] = [...data.assignments]
    .sort((a, b) => String(b.assignedDate).localeCompare(String(a.assignedDate)))
    .map((assignment) => ({
      assignment,
      method: methodByCode.get(assignment.methodCode) || null,
      link: activeLink(assignment),
      lastResult: data.results.find((r) => r.methodCode === assignment.methodCode) || null,
    }));
  return { active: rows.filter((r) => isAssignmentActive(r.assignment)), history: rows };
}

/** Пациент, определённый по учётной записи (кабинет пациента). */
export interface SelfPatient {
  patientId: string;
  patientName: string;
  matchedBy: 'phone' | 'email';
}

export type SelfPatientResolution =
  | { status: 'found'; patient: SelfPatient }
  | { status: 'none' }
  | { status: 'ambiguous'; count: number };

function digitsOf(value: string): string {
  return String(value || '').replace(/\D+/g, '');
}

function normalizeEmail(value: string): string {
  return String(value || '').trim().toLowerCase();
}

async function patientRows(filter?: { column: string; value: string }): Promise<Array<Record<string, string> & { id: string }>> {
  const r = await getSDK().metadata.listTableData({
    schema: 'public', table: PATIENTS, pageSize: 500, page: 0,
    filters: filter ? [filter] : [],
  });
  return rowsOf(r.rows);
}

/**
 * Определить карточку пациента текущего пользователя (кабинет пациента).
 *
 * В ядре «ГидраМедик» у карточки пациента НЕТ прямой ссылки на пользователя,
 * поэтому связь детерминированно устанавливается по контактам учётной записи:
 * телефон (сравнение по цифрам) ЛИБО почта (без учёта регистра). Сначала —
 * точечные серверные отборы, затем сравнение нормализованных значений по
 * странице реестра. Несколько разных карточек — НЕ выбираем одну (медицинские
 * данные): возвращается ambiguous, кабинет просит обратиться к специалисту.
 */
export async function resolveMyPatient(phone: string, email: string): Promise<SelfPatientResolution> {
  const phoneKey = digitsOf(phone);
  const emailKey = normalizeEmail(email);
  if (!phoneKey && !emailKey) return { status: 'none' };

  const candidates = new Map<string, SelfPatient>();
  const consider = (row: Record<string, string> & { id: string }, matchedBy: 'phone' | 'email') => {
    if (!row.id || candidates.has(row.id)) return;
    candidates.set(row.id, {
      patientId: row.id,
      patientName: String(row.name || row.fio || 'Пациент'),
      matchedBy,
    });
  };

  if (phone) {
    for (const row of await patientRows({ column: 'phone', value: phone })) {
      if (digitsOf(row.phone) === phoneKey) consider(row, 'phone');
    }
  }
  if (email) {
    for (const row of await patientRows({ column: 'email', value: email })) {
      if (normalizeEmail(row.email) === emailKey) consider(row, 'email');
    }
  }
  if (candidates.size === 0) {
    for (const row of await patientRows()) {
      if (phoneKey && digitsOf(row.phone) === phoneKey) consider(row, 'phone');
      else if (emailKey && normalizeEmail(row.email) === emailKey) consider(row, 'email');
    }
  }

  if (candidates.size === 0) return { status: 'none' };
  if (candidates.size > 1) return { status: 'ambiguous', count: candidates.size };
  const [patient] = [...candidates.values()];
  return { status: 'found', patient };
}

/** Признак установленного модуля психологии (справочник назначений) — кэш по базе. */
const psyModuleCache = new Map<string, boolean>();

export async function hasPsyAssignmentsModule(): Promise<boolean> {
  const key = slug();
  const cached = psyModuleCache.get(key);
  if (cached !== undefined) return cached;
  const r = await getSDK().metadata.listCatalogs({});
  const has = r.items.some((c) => String(c.code || '') === ASSIGNMENTS);
  psyModuleCache.set(key, has);
  return has;
}

/**
 * Назначить методику (идемпотентно): активное назначение пациента+методики
 * переиспользуется, иначе создаётся строка psy_assignments.
 */
export async function ensureAssignment(
  patientId: string, patientName: string, m: PsyMethod,
  existing: PsyAssignment[],
): Promise<PsyAssignment> {
  const current = assignmentFor(existing, m.code);
  if (current && current.status === 'assigned') return current;
  // Свежая проверка перед созданием (защита от дубля при устаревшем списке/
  // параллельном окне): активное назначение переиспользуется.
  const fresh = await loadAssignments(patientId);
  const active = fresh.find((a) => a.methodCode === m.code && a.status === 'assigned');
  if (active) return active;
  const row = await dataService.create('public', ASSIGNMENTS, {
    name: `${m.synonym} — ${patientName}`.slice(0, 120),
    patient_id: patientId,
    method_code: m.code,
    assigned_date: new Date().toISOString().slice(0, 10),
    status: 'assigned',
  });
  const id = String(row.id || '');
  return {
    id, patientId, methodCode: m.code, status: 'assigned',
    assignedDate: new Date().toISOString().slice(0, 10), linkToken: '', linkExpiresAt: '',
  };
}

/** Сохранить токен/срок ссылки в назначении (служебные поля). */
export async function saveAssignmentLink(id: string, token: string, expiresAt: string): Promise<void> {
  if (!id) return;
  await dataService.update('public', ASSIGNMENTS, id, {
    link_token: token,
    link_expires_at: expiresAt,
  });
}

/** Вопросы шаблона (для решения «онлайн-тест или ручной результат»). */
export async function methodQuestionCount(m: PsyMethod): Promise<number> {
  const full = await getSDK().poll.getPollFull({ id: m.templateId });
  return full.questions.length;
}
